<template>
  <a-modal title="Change Training" :visible="visible" width="500px" @cancel="cancel" :destroy-on-close="true">
    <actualize-modal
      :visible.sync="visibleActualizeModal"
      :show-support="false"
      :initial-start-date="form.training_started_at"
      :initial-end-date="form.training_ended_at"
      initial-rate-type="training_rate"
      @update="cancel"
      @close="cancel"
    />

    <a-form-model
      layout="horizontal"
      ref="form"
      class="flex gap-6"
      :model="form"
      :rules="rules"
      :label-col="{ span: 8 }"
      :wrapper-col="{ span: 16 }"
    >
      <div class="flex flex-column gap-2">
        <a-form-model-item :colon="false" label-align="left" label="Training Started At" prop="training_started_at">
          <a-date-picker
            class="w-full"
            :allowClear="true"
            :format="visualDateFormat"
            placeholder="Training Started At"
            v-model="form.training_started_at"
            valueFormat="YYYY-MM-DD"
          />
        </a-form-model-item>
        <a-form-model-item :colon="false" label-align="left" label="Training Ended At" prop="training_ended_at">
          <a-date-picker
            class="w-full"
            :allowClear="true"
            :format="visualDateFormat"
            :disabled-date="disabledEndDate"
            placeholder="Training Ended At"
            v-model="form.training_ended_at"
            valueFormat="YYYY-MM-DD"
          />
        </a-form-model-item>
      </div>
    </a-form-model>

    <template slot="footer">
      <div class="flex justify-content-between">
        <a-button @click="cancel"> Cancel </a-button>
        <a-button icon="save" type="primary" @click="submit" :loading="loading"> Submit </a-button>
      </div>
    </template>
  </a-modal>
</template>

<script>
import { deepCopy } from '@/utils/deep-copy'
import { Button, DatePicker, FormModel, Modal, notification } from 'ant-design-vue'
import moment from 'moment-timezone'
import { createNamespacedHelpers } from 'vuex'
import ProfileActualizeShift from '../profile-actualize-shift.vue'

const { mapState: profileState, mapActions: profileActions } = createNamespacedHelpers('profile')
const { mapState: documentState } = createNamespacedHelpers('document')
const { mapActions: modalActions } = createNamespacedHelpers('modals')

const initialForm = {
  training_started_at: undefined,
  training_ended_at: undefined
}

const initialRules = {
  training_started_at: [
    {
      required: true,
      message: 'Can not be empty',
      trigger: 'change'
    }
  ]
}

export default {
  components: {
    'a-modal': Modal,
    'a-form-model': FormModel,
    'a-form-model-item': FormModel.Item,
    'a-button': Button,
    'a-date-picker': DatePicker,
    'actualize-modal': ProfileActualizeShift
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,

      form: { ...initialForm },
      rules: { ...initialRules },

      visibleActualizeModal: false
    }
  },
  watch: {
    visible: {
      handler(newVal) {
        if (newVal) {
          this.form = deepCopy(this.associate)
        }
      },
      immediate: true
    }
  },
  computed: {
    ...profileState({
      associate: state => state.associate
    }),
    ...documentState({
      skills_check: state => state.associateDynamicForm?.skills_check,
      availability_skills_check: state => state.associateDynamicFormAvailability?.skills_check
    }),

    visualDateFormat() {
      return this.$store.state.applicationState.dateFormat
    },

    isStaff() {
      return this.$store.state.applicationState.currentProject.staffing
    },

    shouldTriggerSkillsCheck() {
      const isCheckAvailable = this.availability_skills_check && !this.skills_check && !this.isStaff

      if (!isCheckAvailable) return false

      const isTrainingExpired =
        this.form.training_ended_at && moment(this.form.training_ended_at).isBefore(moment(), 'day')

      return isTrainingExpired
    }
  },
  methods: {
    ...profileActions(['updateAssociate', 'setAssociate']),
    ...modalActions(['openModal']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    disabledEndDate(current) {
      if (!this.form.training_started_at) {
        return true
      }
      const startDate = moment(this.form.training_started_at)
      const minEndDate = startDate.clone().startOf('day')
      return current && current.isBefore(minEndDate)
    },

    cancel() {
      this.form = deepCopy(initialForm)
      this.$emit('update:visible', false)
    },

    async saveAssociate() {
      const payload = {
        associate: {
          ...this.associate,
          ...this.form
        }
      }

      const data = await this.updateAssociate(payload)
      this.setAssociate({ ...this.associate, ...data })
    },

    async openActualizeShift() {
      Modal.confirm({
        title: 'Review unpaid shift rates',
        content: 'Choose a rate and period, then review the changes before applying them to unpaid shifts.',
        okText: 'Review rates',
        cancelText: 'Close',
        onOk: () => {
          this.visibleActualizeModal = true
        },
        onCancel: () => {
          this.cancel()
        }
      })
    },

    async submit() {
      const isValid = await this.validateForm()
      if (!isValid) return

      try {
        if (this.shouldTriggerSkillsCheck) {
          await this.showSkillsCheckModal()
          return
        }

        this.loading = true

        await this.saveAssociate()

        this.openActualizeShift()
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error.message)
      } finally {
        this.loading = false
      }
    },

    async validateForm() {
      return new Promise(resolve => {
        this.$refs.form.validate(valid => resolve(valid))
      })
    },

    async showSkillsCheckModal() {
      return new Promise((resolve, reject) => {
        this.openModal({
          type: 'dynamicForm',
          data: {
            visible: true,
            type: 'skills_check',
            initialValue: { associate_id: this.associate.id },
            save: async () => {
              try {
                await this.saveAssociate()
                this.cancel()
                resolve()
              } catch (error) {
                reject(error)
              }
            },
            cancel: () => {}
          }
        })
      })
    }
  }
}
</script>
<style scoped>
.ant-row {
  display: flex;
  margin: 0 !important;
}

.ant-form {
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
}
</style>
